/** A note's comments (decrypted in the browser for end-to-end encrypted notes), and how to add or resolve them. */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { del, get, patch, post } from "@/lib/api";
import { qk, useNote } from "@/lib/queries";
import { useIsUnlocked } from "@/features/crypto/keyring";
import { decAnchor, decComment, encAnchor, encComment, proofFor } from "@/features/crypto/ops";

export interface NoteComment {
  id: string;
  body: string;
  anchorQuote: string | null;
  encrypted?: boolean;
  authorId: string;
  authorName: string;
  createdAt: string;
  resolvedAt: string | null;
}

export function useNoteComments(noteId: string) {
  const qc = useQueryClient();
  const note = useNote(noteId);
  const scopeId = note.data?.note.encryptionScopeId ?? null;
  const unlocked = useIsUnlocked(scopeId);
  const query = useQuery({
    queryKey: ["comments", noteId, scopeId ? (unlocked ? "unlocked" : "locked") : "plain"],
    enabled: !!note.data && (!scopeId || unlocked),
    // Comments from other members appear without reloading.
    refetchInterval: 20_000,
    queryFn: async () => {
      const r = await get<{ comments: NoteComment[] }>(`/api/notes/${noteId}/comments`);
      if (!scopeId) return r;
      const out: NoteComment[] = [];
      for (const c of r.comments) {
        if (!c.encrypted) out.push(c);
        else
          out.push({
            ...c,
            body: await decComment(scopeId, noteId, c.id, c.body).catch(() => "(this comment could not be decrypted)"),
            anchorQuote: c.anchorQuote ? await decAnchor(scopeId, noteId, c.id, c.anchorQuote).catch(() => null) : null,
          });
      }
      return { comments: out };
    },
  });
  const refresh = () => Promise.all([qc.invalidateQueries({ queryKey: ["comments", noteId] }), qc.invalidateQueries({ queryKey: qk.note(noteId) })]);
  return {
    note,
    scopeId,
    unlocked,
    comments: query.data?.comments ?? [],
    loaded: !!query.data,
    async add(body: string, anchor: string | null) {
      if (scopeId) {
        const id = crypto.randomUUID();
        await post(`/api/notes/${noteId}/comments`, {
          id,
          body: await encComment(scopeId, noteId, id, body),
          anchorQuote: anchor ? await encAnchor(scopeId, noteId, id, anchor) : null,
          keyProof: proofFor(scopeId),
        });
      } else await post(`/api/notes/${noteId}/comments`, { body, anchorQuote: anchor });
      await refresh();
    },
    async resolve(id: string, resolved: boolean) {
      await patch(`/api/comments/${id}`, { resolved });
      await refresh();
    },
    async remove(id: string) {
      await del(`/api/comments/${id}`);
      await refresh();
    },
  };
}
